import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

export const JSDestructuring: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Destructuring</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    Unpack values from objects and arrays in one concise line — aliasing, defaults, nesting, parameter destructuring, and the patterns React and Node.js lean on constantly.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="16 min read" />
  </div>

  {/* 1. Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>A real API response is nested, and pulling values out the manual way is repetitive:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const order = {\n  id: 101,\n  customer: { name: "Sachin", email: "sachin@gmail.com" },\n  deliveryAddress: { city: "Bangalore", state: "Karnataka" },\n};\n\nconst name = order.customer.name;\nconst email = order.customer.email;\nconst city = order.deliveryAddress.city; // boilerplate everywhere` }]} defaultLanguage="javascript" id="de-problem" />
   <Para>As apps grow — more data, more nesting — this boilerplate multiplies. Destructuring solves it elegantly.</Para>
  </section>

  {/* 2. Definition */}
  <section className="mb-12">
   <SectionHeading n={2}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200"><Mark>Destructuring</Mark> extracts values from objects or arrays into individual variables — same result as manual access, with far less code.</p>
   </div>
   <CodeBlock snippets={[{ language: 'javascript', code: `// before\nconst name = user.name;\nconst age = user.age;\n\n// after\nconst { name, age } = user;` }]} defaultLanguage="javascript" id="de-definition" />
  </section>

  {/* 3. Why */}
  <section className="mb-12">
   <SectionHeading n={3}>Why It Exists</SectionHeading>
   <Para>It collapses repetitive property access into one readable line:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const { name, age, city } = user;` }]} defaultLanguage="javascript" id="de-why" />
   <div className="flex flex-wrap gap-2">{['Cleaner code', 'Less repetition', 'Better readability', 'Ubiquitous in React', 'Common in Node.js'].map((b) => <Pill key={b}>{b}</Pill>)}</div>
  </section>

  {/* 4. Core idea */}
  <section className="mb-12">
   <SectionHeading n={4}>Core Idea — Unpacking</SectionHeading>
   <Para>Think of destructuring as <Mark>unpacking</Mark> values out of a container into named variables:</Para>
   <pre className="my-2 text-[13px] font-mono text-gray-600 dark:text-gray-400">{`{ name: "Sachin", city: "Bangalore" }\n        │              │\n   const { name,        city } = user;`}</pre>
  </section>

  {/* 5. Object basics */}
  <section className="mb-12">
   <SectionHeading n={5}>Object Destructuring Basics</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = { name: "Sachin", age: 28, city: "Bangalore" };\n\nconst { name, age, city } = user;\n// name -> "Sachin", age -> 28, city -> "Bangalore"` }]} defaultLanguage="javascript" id="de-obj" />
  </section>

  {/* 6. Name matching */}
  <section className="mb-12">
   <SectionHeading n={6}>Property Names Must Match</SectionHeading>
   <Para>The variable name must match the property key — otherwise you get <Code>undefined</Code>:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = { name: "Sachin" };\n\nconst { name } = user;     // "Sachin"\nconst { username } = user; // undefined — no such key` }]} defaultLanguage="javascript" id="de-match" />
  </section>

  {/* 7. Aliasing */}
  <section className="mb-12">
   <SectionHeading n={7}>Aliasing (Renaming)</SectionHeading>
   <Para>Rename a property into a different variable with <Code>:</Code>:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = { name: "Sachin" };\n\nconst { name: userName } = user;\nconsole.log(userName); // "Sachin"  (there is no "name" variable)` }]} defaultLanguage="javascript" id="de-alias" />
  </section>

  {/* 8. Defaults */}
  <section className="mb-12">
   <SectionHeading n={8}>Default Values</SectionHeading>
   <Para>Provide a fallback for missing properties — great for optional/config fields:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = { name: "Sachin" };\n\nconst { city } = user;           // undefined\nconst { city = "Delhi" } = user; // "Delhi"` }]} defaultLanguage="javascript" id="de-default" />
  </section>

  {/* 9. Nested */}
  <section className="mb-12">
   <SectionHeading n={9}>Nested Object Destructuring</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = {\n  name: "Sachin",\n  address: { city: "Bangalore", state: "Karnataka" },\n};\n\nconst { address: { city, state } } = user;\n// city -> "Bangalore", state -> "Karnataka"` }]} defaultLanguage="javascript" id="de-nested" />
   <Callout tone="info">Note: <Code>address:</Code> here is a <Mark>path</Mark>, not a new variable. <Code>address</Code> itself is not created — only <Code>city</Code> and <Code>state</Code>.</Callout>
  </section>

  {/* 10. Array destructuring */}
  <section className="mb-12">
   <SectionHeading n={10}>Array Destructuring</SectionHeading>
   <Para>Arrays destructure by <Mark>position</Mark> (not name):</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const fruits = ["Apple", "Banana", "Mango"];\n\nconst [first, second] = fruits;\n// first -> "Apple", second -> "Banana"` }]} defaultLanguage="javascript" id="de-arr" />
  </section>

  {/* 11. Skipping + defaults */}
  <section className="mb-12">
   <SectionHeading n={11}>Skipping Values &amp; Array Defaults</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const fruits = ["Apple", "Banana", "Mango"];\n\nconst [first, , third] = fruits; // skip index 1 -> "Apple", "Mango"\n\nconst [a, b = "Banana"] = ["Apple"]; // default -> "Apple", "Banana"` }]} defaultLanguage="javascript" id="de-skip" />
  </section>

  {/* 12. Swapping */}
  <section className="mb-12">
   <SectionHeading n={12}>Swapping Variables</SectionHeading>
   <Para>A classic interview trick — swap without a temp variable:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `let a = 10;\nlet b = 20;\n\n[a, b] = [b, a];\n// a -> 20, b -> 10` }]} defaultLanguage="javascript" id="de-swap" />
  </section>

  {/* 13. Function params */}
  <section className="mb-12">
   <SectionHeading n={13}>Function Parameter Destructuring</SectionHeading>
   <Para>Destructure right in the parameter list for clean, self-documenting APIs:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function createUser({ name, city }) {\n  console.log(name);\n  console.log(city);\n}\n\ncreateUser({ name: "Sachin", city: "Bangalore" });` }]} defaultLanguage="javascript" id="de-params" />
   <Output>{`Sachin\nBangalore`}</Output>
  </section>

  {/* 14. React + API */}
  <section className="mb-12">
   <SectionHeading n={14}>React &amp; API Patterns</SectionHeading>
   <Para>React components destructure props constantly, and Node.js code unpacks API responses the same way:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `// React: destructure props in the signature\nfunction UserCard({ name, city }) {\n  return <div>{name} — {city}</div>;\n}\n\n// Node: unpack a nested API response\nconst response = { success: true, data: { userId: 101, name: "Sachin" } };\nconst { data: { userId, name } } = response;` }]} defaultLanguage="javascript" id="de-react" />
  </section>

  {/* 15. Across languages */}
  <section className="mb-12">
   <SectionHeading n={15}>Across Languages</SectionHeading>
   <CodeBlock snippets={[{ language: 'typescript', code: `interface User { name: string; city: string; }\n\nconst user: User = { name: "Sachin", city: "Bangalore" };\nconst { name, city } = user; // typed automatically` }]} defaultLanguage="typescript" id="de-ts" />
   <CodeBlock snippets={[{ language: 'java', code: `// Java has no native destructuring:\nUser user = new User("Sachin", "Bangalore");\nString name = user.getName();\nString city = user.getCity();` }]} defaultLanguage="java" id="de-java" />
   <Callout tone="info">JavaScript&apos;s destructuring removes a lot of the getter boilerplate Java requires.</Callout>
  </section>

  {/* 16. Common mistakes */}
  <section className="mb-12">
   <SectionHeading n={16}>Common Mistakes</SectionHeading>
   <Callout tone="warning" title="Wrong property names">A mismatched key gives <Code>undefined</Code> — use aliasing to rename intentionally.</Callout>
   <Callout tone="warning" title="Destructuring null/undefined"><Code>const {`{ name }`} = null</Code> throws a <Code>TypeError</Code>. Default the whole object: <Code>const {`{ name }`} = obj || {`{}`}</Code>.</Callout>
   <Callout tone="warning" title="Too much nesting">Deep patterns like <Code>{`{ a: { b: { c } } }`}</Code> hurt readability — destructure a level at a time.</Callout>
   <Callout tone="warning" title="Mixing {} and []">Objects destructure with <Code>{`{}`}</Code> (by name), arrays with <Code>[]</Code> (by position).</Callout>
  </section>

  {/* 17. Real interview questions */}
  <section className="mb-12">
   <SectionHeading n={17}>Real Interview Questions</SectionHeading>
   <QA q="What is destructuring and why use it?">
    A syntax to extract values from objects/arrays into variables in one concise, readable line — it removes repetitive property access.
   </QA>
   <QA q="How is object destructuring different from array destructuring?">
    Objects destructure by <Mark>property name</Mark> with <Code>{`{}`}</Code>; arrays destructure by <Mark>position</Mark> with <Code>[]</Code>.
   </QA>
   <QA q="How do you rename a variable while destructuring?">
    Use a colon: <Code>const {`{ name: userName }`} = user</Code> creates <Code>userName</Code>.
   </QA>
   <QA q="How do you swap two variables without a temp?">
    <Code>[a, b] = [b, a]</Code> using array destructuring.
   </QA>
   <QA q="What happens when you destructure a missing property?">
    You get <Code>undefined</Code>, unless you supply a default value like <Code>const {`{ city = "Delhi" }`} = user</Code>.
   </QA>
   <QA q="Why does destructuring null throw an error?">
    You can&apos;t read properties off <Code>null</Code>/<Code>undefined</Code>. Guard with <Code>= obj || {`{}`}</Code> or optional chaining before destructuring.
   </QA>
  </section>

  {/* 18. Mental model */}
  <section className="mb-12">
   <SectionHeading n={18}>Mental Model</SectionHeading>
   <Para>Think of a delivery box. The manual way opens the box once per item. Destructuring <Mark>unpacks everything at once</Mark> into clearly labeled variables — faster and far more readable.</Para>
  </section>

  {/* 19 & 20 */}
  <section className="mb-4">
   <SectionHeading n={19}>Interview One-Liner &amp; Takeaway</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <div className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-2">Interview One-Liner</div>
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     Destructuring extracts values from objects and arrays into variables using concise, readable syntax.
    </p>
   </div>
   <Callout tone="info" title="Most important takeaway">
    Destructuring eliminates repetitive property access and is everywhere in React, Node.js, API handling, config, and function parameters. If you work with objects and arrays often, it&apos;s one of the most valuable syntax features to master.
   </Callout>
  </section>
 </div>
);

export default JSDestructuring;
